import { useState, useEffect, useMemo } from "react";
import InnerPageLayout from "../../components/feature/InnerPageLayout";
import BlogCard from "./components/BlogCard";
import CategoryFilter from "./components/CategoryFilter";
import { blogPosts, blogCategories } from "../../mocks/blog";

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState("전체");
  const [searchQuery, setSearchQuery] = useState("");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    document.title = "아르트레너 블로그 | 아티스트 비즈니스 창업 인사이트";
    const metaDesc = document.querySelector("meta[name='description']");
    if (metaDesc) {
      metaDesc.setAttribute("content", "예술가 비즈니스 창업, 아티스트 수익화 전략, 문화예술 창업 노하우를 아르트레너 전문가들이 직접 씁니다. 200명 파트너 데이터 기반 실전 인사이트.");
    }
  }, []);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { "전체": blogPosts.length };
    blogCategories.slice(1).forEach((cat) => {
      counts[cat] = blogPosts.filter((p) => p.category === cat).length;
    });
    return counts;
  }, []);

  const filtered = useMemo(() => {
    return blogPosts.filter((post) => {
      const matchCat = activeCategory === "전체" || post.category === activeCategory;
      const matchSearch = !searchQuery.trim() || [post.title, post.excerpt, ...post.tags].some((s) =>
        s.toLowerCase().includes(searchQuery.toLowerCase())
      );
      return matchCat && matchSearch;
    });
  }, [activeCategory, searchQuery]);

  const featuredPost = blogPosts.find((p) => p.featured);
  const regularPosts = filtered.filter((p) => !(p.featured && activeCategory === "전체" && !searchQuery));

  return (
    <InnerPageLayout>
      {/* SEO structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "아르트레너 블로그",
            "description": "예술가 비즈니스 창업, 아티스트 수익화 전략, 문화예술 창업 노하우",
            "url": "https://artrepreneur.kr/blog",
            "publisher": {
              "@type": "Organization",
              "name": "ARTREPRENEUR",
              "logo": "https://public.readdy.ai/ai/img_res/b51260fb-bf77-436f-865d-1c38bc3c656b.png"
            }
          })
        }}
      />

      {/* Hero */}
      <section className="relative py-20 lg:py-32 px-4 md:px-6 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 60% 30%, rgba(168,85,247,0.08) 0%, transparent 60%), radial-gradient(ellipse at 20% 80%, rgba(20,241,217,0.05) 0%, transparent 50%)" }} />
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-display font-medium tracking-widest uppercase text-brand-teal mb-6 px-3 py-1 rounded-full border border-brand-teal/30 bg-brand-teal/5">
              ARTREPRENEUR BLOG
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-snug lg:leading-relaxed">
              아티스트가 CEO가<br />
              <span className="gradient-text-brand">되는 인사이트</span>
            </h1>
            <p className="mt-6 text-white/50 font-body text-base leading-loose max-w-xl">
              200명 이상의 파트너 아티스트와 함께 쌓은 실전 데이터. 예술가 비즈니스 창업과 문화예술 수익화의 모든 노하우를 공유합니다.
            </p>
          </div>

          {/* Search */}
          <div className="mt-10 max-w-lg">
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-white/30">
                <i className="ri-search-line text-base" />
              </div>
              <input
                type="text"
                placeholder="아티스트 창업, 수익화, 법무 등 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-5 py-3.5 rounded-2xl text-sm font-body text-white placeholder-white/25 outline-none transition-all duration-300 border focus:border-brand-purple/50"
                style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.1)" }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-base" />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 lg:pb-32 px-4 md:px-6">
        <div className="max-w-7xl mx-auto flex flex-col gap-10">

          {/* Category Filter */}
          <CategoryFilter
            active={activeCategory}
            onChange={setActiveCategory}
            counts={categoryCounts}
          />

          {/* Featured Post */}
          {featuredPost && activeCategory === "전체" && !searchQuery && mounted && (
            <div
              className="opacity-0 translate-y-4 transition-all duration-700"
              style={{ animation: "fadeInUp 0.6s ease forwards" }}
            >
              <BlogCard post={featuredPost} variant="featured" />
            </div>
          )}

          {/* Grid */}
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl" style={{ background: "rgba(255,255,255,0.05)" }}>
                <i className="ri-search-line text-2xl text-white/30" />
              </div>
              <p className="text-white/40 font-body text-base">검색 결과가 없어요.</p>
              <button
                onClick={() => { setSearchQuery(""); setActiveCategory("전체"); }}
                className="text-sm text-brand-purple hover:text-white transition-colors cursor-pointer font-display"
              >
                전체 글 보기 →
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              {regularPosts.map((post, idx) => (
                <div
                  key={post.id}
                  style={{ animation: `fadeInUp 0.5s ease ${idx * 0.07}s forwards`, opacity: 0 }}
                >
                  <BlogCard post={post} />
                </div>
              ))}
            </div>
          )}

          {/* Stats Bar */}
          <div className="flex flex-wrap items-center justify-center gap-6 lg:gap-12 py-10 border-t border-white/5">
            {[
              { label: "총 아티클", value: `${blogPosts.length}편` },
              { label: "파트너 데이터 기반", value: "200명+" },
              { label: "전문 작성자", value: "2명" },
              { label: "업데이트", value: "매주 1회+" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-display text-2xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-xs text-white/35 font-body">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </InnerPageLayout>
  );
}
